'use client';

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { SField, type SFieldSize, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { SIcon, type SIconName } from '../SIcon';
import { SGhostButton } from '../SGhostButton';
import { type SColor } from '../../lib/color';
import { cn } from '../../lib/cn';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { type STooltipProps } from '../STooltip';
import {
  TIMEPICKER_SIZE_CONFIG,
  clampTimePart,
  formatTimeDisplay,
  formatTimeValue,
  parseTimeValue,
  type STimePickerMeridiem,
  type STimePickerSize,
  type STimePickerType,
} from './timepicker.config';

export type { STimePickerSize, STimePickerType } from './timepicker.config';

const TIME_SELECTOR_WIDTH = 136;
const MERIDIEM_TIME_SELECTOR_WIDTH = 154;

export interface STimePickerProps {
  /** 선택 시간 (HH:mm) */
  value?: string | null;
  /** 선택 변경 (sdUpdate) */
  onValueChange?: (time: string | null) => void;
  /** 열림/닫힘 변경 (sdDropDownShow) */
  onOpenChange?: (open: boolean) => void;
  /** 표시 타입: default(24시간) / midday(오전·오후) */
  type?: STimePickerType;
  size?: STimePickerSize;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** 오전/오후 선택 표시 여부. 지정하지 않으면 type="midday"일 때만 켜집니다. */
  useMeridiem?: boolean;
  minuteStep?: number;
  width?: number | string;
  name?: string;
  rules?: Rule[];
  status?: SFieldStatus;
  // ── SField ──
  label?: string;
  labelWidth?: number | string;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  addonLabel?: string;
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  style?: CSSProperties;
}

export function TimeSelector({
  value,
  useMeridiem,
  minuteStep,
  onChange,
}: {
  value?: string | null;
  useMeridiem: boolean;
  minuteStep: number;
  onChange: (time: string) => void;
}) {
  const parsed = useMemo(() => parseTimeValue(value), [value]);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [editingPart, setEditingPart] = useState<'hour' | 'minute' | null>(null);
  const [inputDraft, setInputDraft] = useState<Partial<Record<'hour' | 'minute', string>>>({});

  useEffect(() => {
    setHour(parsed.hour);
    setMinute(parsed.minute);
  }, [parsed.hour, parsed.minute]);

  const meridiem: STimePickerMeridiem = hour >= 12 ? 'PM' : 'AM';
  const displayHour = useMeridiem ? (hour % 12 === 0 ? 12 : hour % 12) : hour;

  const commit = (nextHour: number, nextMinute: number) => {
    const h = clampTimePart(nextHour, 0, 23);
    const m = clampTimePart(nextMinute, 0, 59);
    setHour(h);
    setMinute(m);
    onChange(formatTimeValue(h, m));
  };

  const setMeridiem = (next: STimePickerMeridiem) => {
    if (next === meridiem) return;
    commit(next === 'AM' ? hour - 12 : hour + 12, minute);
  };

  const changeMinute = (delta: number) => {
    const next = minute + delta;
    if (next > 59) {
      commit((hour + 1) % 24, next - 60);
      return;
    }
    if (next < 0) {
      commit((hour + 23) % 24, next + 60);
      return;
    }
    commit(hour, next);
  };

  const changeHour = (delta: number) => commit((hour + delta + 24) % 24, minute);

  const sanitizeInput = (next: string) => next.replace(/\D/g, '').slice(0, 2);

  const beginEdit = (part: 'hour' | 'minute', currentValue: number) => {
    setEditingPart(part);
    setInputDraft(prev => ({ ...prev, [part]: String(currentValue).padStart(2, '0') }));
  };

  const commitHourDraft = (draft: string) => {
    const clamped = clampTimePart(Number(draft), useMeridiem ? 1 : 0, useMeridiem ? 12 : 23);
    const nextHour = useMeridiem
      ? meridiem === 'PM'
        ? clamped === 12
          ? 12
          : clamped + 12
        : clamped === 12
          ? 0
          : clamped
      : clamped;
    commit(nextHour, minute);
    return clamped;
  };

  const commitMinuteDraft = (draft: string) => {
    const clamped = clampTimePart(Number(draft), 0, 59);
    commit(hour, clamped);
    return clamped;
  };

  const endEdit = (part: 'hour' | 'minute') => {
    const draft = inputDraft[part];
    if (part === 'hour' && draft != null && draft !== '') {
      commitHourDraft(draft);
    }
    if (part === 'minute' && draft != null && draft !== '') {
      commitMinuteDraft(draft);
    }
    setEditingPart(null);
    setInputDraft({});
  };

  const inputStyle = {
    width: 'var(--cmp-timepicker-selector-time-input-width)',
    height: 'var(--cmp-textinput-sm-height)',
    borderRadius: 'var(--cmp-textinput-sm-radius)',
    border: 'var(--cmp-textinput-borderWidth) solid var(--cmp-textinput-border-default)',
    color: 'var(--cmp-textinput-text-default)',
    fontSize: 12,
    lineHeight: '20px',
  } as CSSProperties;

  const renderColumn = (type: 'hour' | 'minute') => {
    const isHour = type === 'hour';
    const val = isHour ? displayHour : minute;
    const displayValue =
      editingPart === type ? (inputDraft[type] ?? '') : String(val).padStart(2, '0');

    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ gap: 'var(--cmp-timepicker-selector-time-column-gap)' }}
      >
        <SGhostButton
          icon="chevronUp"
          size="xs"
          ariaLabel={`${isHour ? '시' : '분'} 증가`}
          onClick={() => (isHour ? changeHour(1) : changeMinute(minuteStep))}
        />
        <input
          aria-label={isHour ? '시' : '분'}
          inputMode="numeric"
          value={displayValue}
          placeholder="-"
          onFocus={() => beginEdit(type, val)}
          onBlur={() => endEdit(type)}
          onChange={event => {
            const draft = sanitizeInput(event.target.value);
            setInputDraft(prev => ({ ...prev, [type]: draft }));
            if (draft === '') return;

            const raw = Number(draft);
            if (isHour) {
              if (draft.length < 2) return;
              const clamped = commitHourDraft(draft);
              const displayDraft = raw === clamped ? draft : String(clamped);
              setInputDraft(prev => ({ ...prev, [type]: displayDraft }));
              return;
            }
            if (draft.length < 2) return;
            const clamped = commitMinuteDraft(draft);
            const displayDraft = raw === clamped ? draft : String(clamped);
            setInputDraft(prev => ({ ...prev, [type]: displayDraft }));
          }}
          className="box-border bg-white text-center outline-none"
          style={inputStyle}
        />
        <SGhostButton
          icon="chevronDown"
          size="xs"
          ariaLabel={`${isHour ? '시' : '분'} 감소`}
          onClick={() => (isHour ? changeHour(-1) : changeMinute(-minuteStep))}
        />
      </div>
    );
  };

  return (
    <div
      className="flex flex-col items-stretch justify-center overflow-hidden"
      style={{
        background: 'var(--cmp-timepicker-selector-bg)',
        borderRadius: 'var(--cmp-timepicker-selector-radius)',
        boxShadow: '4px 8px 16px -4px rgba(34,34,34,0.1), 0 0 24px -6px rgba(34,34,34,0.12)',
      }}
    >
      {useMeridiem && (
        <div
          className="flex items-center justify-center"
          style={{
            padding:
              'var(--cmp-timepicker-selector-section-paddingY) var(--cmp-timepicker-selector-section-paddingX)',
          }}
        >
          <div className="isolate flex">
            {(['AM', 'PM'] as const).map((item, index) => {
              const selected = item === meridiem;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setMeridiem(item)}
                  className={cn(
                    'box-border h-[var(--cmp-textinput-sm-height)] w-[61px] cursor-pointer border border-solid bg-white text-center text-[12px] font-medium leading-[20px]',
                    index === 0
                      ? 'rounded-l-[var(--cmp-select-sm-radius)]'
                      : 'rounded-r-[var(--cmp-select-sm-radius)]',
                    index === 0 ? '-mr-px' : '',
                    selected
                      ? 'z-[1] border-[var(--cmp-timepicker-border-focus)] text-[var(--cmp-timepicker-border-focus)]'
                      : 'border-[var(--cmp-timepicker-border-default)] text-[var(--cmp-field-hint-color)]',
                  )}
                >
                  {item === 'AM' ? '오전' : '오후'}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {useMeridiem && <div className="h-px w-full bg-[var(--sys-color-divider-default)]" />}
      <div
        className="flex items-center justify-center"
        style={{
          gap: 'var(--cmp-timepicker-selector-time-gap)',
          padding:
            'var(--cmp-timepicker-selector-section-paddingY) var(--cmp-timepicker-selector-section-paddingX)',
        }}
      >
        {renderColumn('hour')}
        <span
          className="text-center"
          style={{
            width: 'var(--cmp-timepicker-selector-colon-size)',
            color: 'var(--cmp-field-hint-color)',
            fontSize: 'var(--cmp-timepicker-selector-colon-size)',
            lineHeight: 'var(--cmp-timepicker-selector-colon-size)',
          }}
        >
          :
        </span>
        {renderColumn('minute')}
      </div>
    </div>
  );
}

/** STimePicker — sd-time-picker 포팅. SField + Popover(TimeSelector). */
export function STimePicker({
  value,
  onValueChange,
  onOpenChange,
  type = 'default',
  size = 'sm',
  placeholder = '00:00',
  disabled = false,
  clearable = false,
  useMeridiem,
  minuteStep = 1,
  width,
  name,
  rules,
  status,
  label,
  labelWidth,
  icon,
  iconColor,
  labelTooltip,
  labelTooltipProps,
  addonLabel,
  addonAlign,
  hint,
  error,
  errorMessage,
  className,
  style,
}: STimePickerProps) {
  const portalContainer = usePortalContainer();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [popoverAlignOffset, setPopoverAlignOffset] = useState(0);
  const [focused, setFocused] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const dims = TIMEPICKER_SIZE_CONFIG[size] ?? TIMEPICKER_SIZE_CONFIG.sm;
  const normalized = value
    ? formatTimeValue(parseTimeValue(value).hour, parseTimeValue(value).minute)
    : null;
  const effectiveUseMeridiem = useMeridiem ?? type === 'midday';
  const displayText = formatTimeDisplay(normalized, effectiveUseMeridiem ? 'midday' : 'default');
  const hasValue = normalized != null && normalized !== '';

  useFormField({
    name,
    validate: () => {
      const msg = runRules(value, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const resolvedError = error || ruleError !== '';
  const resolvedStatus =
    status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);

  const commitOpen = (next: boolean) => {
    if (disabled) return;
    if (next) {
      const triggerWidth = triggerRef.current?.getBoundingClientRect().width ?? 0;
      const selectorWidth = effectiveUseMeridiem
        ? MERIDIEM_TIME_SELECTOR_WIDTH
        : TIME_SELECTOR_WIDTH;
      setPopoverAlignOffset(Math.max(0, (triggerWidth - selectorWidth) / 2));
    }
    setOpen(next);
    onOpenChange?.(next);
    if (!next && rules && rules.length > 0) setRuleError(runRules(value, rules));
  };

  const commitValue = (next: string | null) => {
    onValueChange?.(next);
    if (rules && rules.length > 0) setRuleError(runRules(next, rules));
  };

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      size={size as SFieldSize}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={focused || open}
      errorMessage={ruleError !== '' ? ruleError : errorMessage}
      width={width}
      minWidth={dims.fieldMinWidth}
      disabled={disabled}
      className={className}
      style={style}
    >
      <RPopover.Root open={open} onOpenChange={commitOpen}>
        <RPopover.Trigger asChild>
          <div
            ref={triggerRef}
            role="button"
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            data-name={name}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={event => {
              if (disabled) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                commitOpen(!open);
              }
            }}
            className={cn(
              'flex h-full w-full items-center overflow-hidden bg-transparent outline-none',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            style={{ padding: `0 ${dims.paddingX}`, gap: dims.gap, borderRadius: dims.radius }}
          >
            <SIcon
              name="clockOutline"
              size={dims.icon}
              color={
                disabled
                  ? 'var(--cmp-timepicker-icon-disabled)'
                  : 'var(--cmp-timepicker-icon-default)'
              }
              className="flex-shrink-0"
            />
            <span
              className="flex-1 truncate text-center"
              style={{
                minWidth: dims.minWidth,
                fontSize: dims.fontSize,
                lineHeight: `${dims.lineHeight}px`,
                color: disabled
                  ? 'var(--cmp-timepicker-text-disabled)'
                  : hasValue
                    ? 'var(--cmp-timepicker-text-default)'
                    : 'var(--cmp-field-hint-color)',
              }}
            >
              {hasValue ? displayText : placeholder}
            </span>
            {clearable && hasValue && !disabled && (
              <SGhostButton
                icon="close"
                size="xs"
                ariaLabel="시간 지우기"
                onClick={event => {
                  event.stopPropagation();
                  commitValue(null);
                }}
              />
            )}
          </div>
        </RPopover.Trigger>
        <RPopover.Portal container={portalContainer}>
          <RPopover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            alignOffset={popoverAlignOffset}
            className={cn('z-50', FLOATING_SLIDE_ANIM)}
          >
            <TimeSelector
              value={normalized}
              useMeridiem={effectiveUseMeridiem}
              minuteStep={minuteStep}
              onChange={commitValue}
            />
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
    </SField>
  );
}
